/* eslint-disable react/no-array-index-key */
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { Typography, Select, MenuItem, FormControl, Box, TextField } from "@mui/material";

const CsvMapping = ({
  unmappedHeaders,
  fieldMappings,
  enrichmentMappings,
  consentMappings,
  defaultSchemaAttributes,
  enrichmentTypes,
  onDragEnd,
  handleEnrichmentChange,
  handleConsentMappingChange,
  availableConsentTypes,
  formValues,
  validationErrors = [],
}) => {
  const getSchemaFieldByHashIdentifier = (hashIdentifier) => {
    const mapping = {
      phone: "phoneNumber",
      email: "email",
    };
    return mapping[hashIdentifier] || hashIdentifier;
  };

  const hasValidationError = (schemaField) => {
    return validationErrors.some((error) =>
      error.toLowerCase().includes(schemaField.toLowerCase())
    );
  };

  const getSchemaFieldStyle = (schemaField) => {
    const requiredFields = ["dateOfBirth", "country"];
    const hashIdentifierField = formValues.hashIdentifier;
    const mappedSchemaFieldForHashIdentifier = getSchemaFieldByHashIdentifier(hashIdentifierField);
    const mappedSchemaValues = fieldMappings.map((mapping) => mapping.schemaValue);

    const isRequiredField = requiredFields.includes(schemaField);
    const isHashIdentifier = schemaField === mappedSchemaFieldForHashIdentifier;
    const isMapped = mappedSchemaValues.includes(schemaField);
    const hasError = hasValidationError(schemaField);

    let style = {};

    if (hasError && isMapped) {
      style = {
        border: "2px solid #d32f2f",
        backgroundColor: "#ffebee",
        borderRadius: "4px",
      };
    } else if (isRequiredField && !isMapped) {
      style = {
        border: "2px solid #ff9800",
        backgroundColor: "#fff3e0",
        borderRadius: "4px",
      };
    } else if (isHashIdentifier && !isMapped) {
      style = {
        border: "2px solid #f44336",
        backgroundColor: "#ffebee",
        borderRadius: "4px",
      };
    } else if ((isRequiredField || isHashIdentifier) && isMapped) {
      style = {
        border: "2px solid #4caf50",
        backgroundColor: "#e8f5e9",
        borderRadius: "4px",
      };
    }

    return style;
  };

  const getSchemaFieldLabel = (schemaField) => {
    const requiredFields = ["dateOfBirth", "country"];
    const hashIdentifierField = formValues.hashIdentifier;
    const mappedSchemaFieldForHashIdentifier = getSchemaFieldByHashIdentifier(hashIdentifierField);
    const hasError = hasValidationError(schemaField);

    let label = schemaField;

    if (requiredFields.includes(schemaField)) {
      label += " *";
    }

    if (schemaField === mappedSchemaFieldForHashIdentifier) {
      label += " (Hash ID)";
    }

    if (hasError) {
      label += " ⚠️";
    }

    return label;
  };

  return (
    <div className="mapping-section">
      <Typography variant="h5" component="h3">
        Map CSV Headers to Prospect Schema
      </Typography>
      <Box sx={{ mb: 2, p: 1.5, backgroundColor: "#fafafa", borderRadius: 1 }}>
        <Typography variant="caption" sx={{ fontWeight: "bold", display: "block", mb: 1 }}>
          Field indicators:
        </Typography>
        <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              sx={{
                width: 16,
                height: 16,
                border: "2px solid #ff9800",
                backgroundColor: "#fff3e0",
                borderRadius: "2px",
              }}
            />
            <Typography variant="caption">Required field (missing)</Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              sx={{
                width: 16,
                height: 16,
                border: "2px solid #f44336",
                backgroundColor: "#ffebee",
                borderRadius: "2px",
              }}
            />
            <Typography variant="caption">Hash identifier (missing)</Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              sx={{
                width: 16,
                height: 16,
                border: "2px solid #4caf50",
                backgroundColor: "#e8f5e9",
                borderRadius: "2px",
              }}
            />
            <Typography variant="caption">Required/Hash ID (mapped)</Typography>
          </Box>
          {validationErrors.length > 0 && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  border: "2px solid #d32f2f",
                  backgroundColor: "#ffebee",
                  borderRadius: "2px",
                }}
              />
              <Typography variant="caption">Validation error</Typography>
            </Box>
          )}
        </Box>
      </Box>
      <div className="mapping-container">
        <DragDropContext onDragEnd={onDragEnd}>
          <div className="mapping-columns">
            <div className="mapping-column">
              <Droppable droppableId="unmapped">
                {(unmappedProvided) => (
                  <div
                    className="droppable-column"
                    ref={unmappedProvided.innerRef}
                    {...unmappedProvided.droppableProps}
                  >
                    <Typography variant="h6" component="h4">
                      Unmapped Headers
                    </Typography>
                    {unmappedHeaders.map((header, index) => (
                      <Draggable key={header} draggableId={header} index={index}>
                        {(unmappedDraggableProvided) => (
                          <div
                            className="draggable-item"
                            ref={unmappedDraggableProvided.innerRef}
                            {...unmappedDraggableProvided.draggableProps}
                            {...unmappedDraggableProvided.dragHandleProps}
                          >
                            <span>{header}</span>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {unmappedProvided.placeholder}
                  </div>
                )}
              </Droppable>
            </div>
            <div className="mapping-column schema-attributes-container">
              <Typography variant="h6" component="h4">
                Schema Attributes
              </Typography>
              {defaultSchemaAttributes.map((attribute) => (
                <Droppable droppableId={attribute} key={attribute}>
                  {(schemaProvided) => (
                    <div
                      className="droppable-column"
                      ref={schemaProvided.innerRef}
                      {...schemaProvided.droppableProps}
                      style={getSchemaFieldStyle(attribute)}
                    >
                      <div className="schema-attribute-header">
                        {getSchemaFieldLabel(attribute)}
                      </div>
                      {hasValidationError(attribute) && (
                        <Typography
                          variant="caption"
                          color="error"
                          sx={{
                            display: "block",
                            fontSize: "0.7rem",
                            marginTop: -1,
                            marginBottom: 1,
                          }}
                        >
                          Invalid data format for this field
                        </Typography>
                      )}
                      {fieldMappings
                        .filter((item) => item.schemaValue === attribute)
                        .map((item, index) => (
                          <Draggable
                            key={`${item.customValue}-${index}`}
                            draggableId={item.customValue}
                            index={index}
                          >
                            {(schemaDraggableProvided) => (
                              <div
                                className="draggable-item"
                                ref={schemaDraggableProvided.innerRef}
                                {...schemaDraggableProvided.draggableProps}
                                {...schemaDraggableProvided.dragHandleProps}
                              >
                                <span>{item.customValue}</span>
                                {hasValidationError(attribute) && (
                                  <Typography
                                    variant="caption"
                                    color="error"
                                    sx={{ display: "block", fontSize: "0.7rem" }}
                                  >
                                    Example: &quot;{item.exampleData}&quot; (invalid)
                                  </Typography>
                                )}
                              </div>
                            )}
                          </Draggable>
                        ))}
                      {schemaProvided.placeholder}
                    </div>
                  )}
                </Droppable>
              ))}
            </div>
            <div className="mapping-column">
              <Droppable droppableId="enrichment">
                {(enrichmentProvided) => (
                  <div
                    className="droppable-column"
                    ref={enrichmentProvided.innerRef}
                    {...enrichmentProvided.droppableProps}
                  >
                    <Typography variant="h6" component="h4">
                      Enrichment Data
                    </Typography>
                    {enrichmentMappings.map((item, index) => (
                      <Draggable
                        key={`enrichment-${index}`}
                        draggableId={item.header}
                        index={index}
                      >
                        {(enrichmentDraggableProvided) => (
                          <div
                            className="draggable-item enrichment-item"
                            ref={enrichmentDraggableProvided.innerRef}
                            {...enrichmentDraggableProvided.draggableProps}
                            {...enrichmentDraggableProvided.dragHandleProps}
                          >
                            <span>{item.header}</span>
                            <FormControl fullWidth size="small">
                              <Select
                                value={item.category}
                                onChange={(e) =>
                                  handleEnrichmentChange(index, "category", e.target.value)
                                }
                                displayEmpty
                                sx={{ mb: 2 }}
                              >
                                <MenuItem value="">Select Category</MenuItem>
                                <MenuItem value="Interest">Interest</MenuItem>
                                <MenuItem value="Consumption">Consumption</MenuItem>
                                <MenuItem value="Profile Source">Profile Source</MenuItem>
                              </Select>
                            </FormControl>
                            <FormControl fullWidth size="small">
                              <Select
                                value={item.type}
                                onChange={(e) =>
                                  handleEnrichmentChange(index, "type", e.target.value)
                                }
                                displayEmpty
                              >
                                <MenuItem value="">Select Type</MenuItem>
                                {enrichmentTypes.map((type) => (
                                  <MenuItem key={type.value} value={type.display}>
                                    {type.display}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {enrichmentProvided.placeholder}
                  </div>
                )}
              </Droppable>
              <Droppable droppableId="consents">
                {(consentsProvided) => (
                  <div
                    className="droppable-column"
                    ref={consentsProvided.innerRef}
                    {...consentsProvided.droppableProps}
                  >
                    <Typography variant="h6" component="h4">
                      Consent Fields
                    </Typography>
                    {consentMappings.map((item, index) => (
                      <Draggable
                        key={`consent-${item.header}`}
                        draggableId={item.header}
                        index={index}
                      >
                        {(consentDraggableProvided) => (
                          <div
                            className="draggable-item enrichment-item"
                            ref={consentDraggableProvided.innerRef}
                            {...consentDraggableProvided.draggableProps}
                            {...consentDraggableProvided.dragHandleProps}
                          >
                            <span>{item.header}</span>
                            <FormControl fullWidth size="small">
                              <Select
                                value={item.consentType}
                                onChange={(e) =>
                                  handleConsentMappingChange(index, "consentType", e.target.value)
                                }
                                displayEmpty
                                sx={{ mb: item.consentType === "custom" ? 1 : 0 }}
                              >
                                <MenuItem value="">Select Consent Type</MenuItem>
                                {availableConsentTypes.map((consentType) => (
                                  <MenuItem key={consentType} value={consentType}>
                                    {consentType.charAt(0).toUpperCase() + consentType.slice(1)}
                                  </MenuItem>
                                ))}
                                <MenuItem value="custom">Custom</MenuItem>
                              </Select>
                            </FormControl>
                            {item.consentType === "custom" && (
                              <TextField
                                size="small"
                                fullWidth
                                placeholder="Enter custom consent type"
                                value={item.customConsentType || ""}
                                onChange={(e) =>
                                  handleConsentMappingChange(
                                    index,
                                    "customConsentType",
                                    e.target.value
                                  )
                                }
                              />
                            )}
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {consentsProvided.placeholder}
                  </div>
                )}
              </Droppable>
            </div>
          </div>
        </DragDropContext>
      </div>
    </div>
  );
};

export default CsvMapping;
